import { isBuildIn } from '@flowus/common/build-in';
import { cx } from '@flowus/common/cx';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import type { FC } from 'react';
import { Fragment, useMemo } from 'react';
import { Icon } from 'src/common/components/icon';
import { Tooltip } from 'src/common/components/tooltip';
import { CURRENT_DOMAIN, PersonSpaceFreeCapacity } from 'src/common/const';

// 打勾的icon
const selected = <Icon name="IcCheck02" size="middle" />;
const activeColorSelected = <Icon name="IcCheck02" size="middle" className="text-active_color" />;

const useSpacePlanPrice = () => {
  const { t } = useAppTranslation();

  const activeNoLimit = <span className="text-active_color">{t('common.unlimited_times')}</span>;
  const tooltipFc = (str: string, link?: string, popup?: string) => (
    <Tooltip popup={popup} className={cx(link && 'cursor-pointer border-b border-grey6')}>
      <a href={link ? link : undefined} target="_blank" rel="noreferrer">
        {str}
      </a>
    </Tooltip>
  );

  // AI权益
  const AIPricing = {
    title: <span className="text-black">{t('views.settings.upgrade.ai_benefits')}</span>,
    data: [
      [
        tooltipFc(t('views.settings.upgrade.total_count')),
        t('views.settings.upgrade.times_per_month', { count: 5 }),
        activeNoLimit,
        activeNoLimit,
      ],
      [
        tooltipFc(
          t('views.settings.upgrade.ai_ask_space'),
          '',
          t('views.settings.upgrade.ai_ask_space_desc')
        ),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
      [
        tooltipFc(
          t('views.settings.upgrade.ai_ask_wiki'),
          '',
          t('views.settings.upgrade.ai_ask_wiki_desc')
        ),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
      [
        tooltipFc(
          t('views.settings.upgrade.ai_ask_subscription'),
          '',
          t('views.settings.upgrade.ai_ask_subscription_desc')
        ),
        '',
        activeColorSelected,
        activeColorSelected,
      ],
      [
        tooltipFc(t('views.settings.upgrade.ai_write_article')),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
      [
        tooltipFc(t('views.settings.upgrade.ai_continue_writing')),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
      [
        tooltipFc(t('views.settings.upgrade.ai_translate')),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
      [
        tooltipFc(t('views.settings.upgrade.ai_summarize')),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
      [
        tooltipFc(t('views.settings.upgrade.ai_abstract')),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
      [tooltipFc(t('common.ai_explain')), selected, activeColorSelected, activeColorSelected],
      [
        tooltipFc(t('views.settings.upgrade.ai_pdf_summarize')),
        selected,
        activeColorSelected,
        activeColorSelected,
      ],
    ],
  };
  /** 价格表 */
  const fuPriceData = [
    AIPricing,
    // 用量
    {
      title: t('views.settings.upgrade.usage'),
      data: [
        [
          tooltipFc(t('views.settings.upgrade.page_block_count')),
          '5000',
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.space_members'),
            `${CURRENT_DOMAIN}/share/12a522e8-cf0d-4959-af83-28276ead05e6`,
            t('views.settings.upgrade.space_members_desc')
          ),
          t('common.unlimited_text') + t('views.settings.upgrade.need_extra_seats'),
          t('common.unlimited_text') + t('views.settings.upgrade.need_extra_seats'),
          t('common.unlimited_text') + t('views.settings.upgrade.need_extra_seats'),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.external_collaborators'),
            `${CURRENT_DOMAIN}/share/b987ee4d-ee13-44d2-96a9-07161675ae13`,
            t('views.settings.upgrade.external_collaborators_desc')
          ),
          '5',
          '100',
          '250',
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.file_upload'),
            '',
            t('views.settings.upgrade.file_upload_desc')
          ),
          '2MB',
          '5GB',
          '5GB',
        ],
        [tooltipFc(t('common.storage_capacity')), PersonSpaceFreeCapacity, '200GB', '300GB'],
        [
          t('views.settings.upgrade.mindmap_nodes'),
          '300',
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
        [t('views.settings.upgrade.single_block_char_limit'), '100000', '100000', '100000'],
      ],
    },
    // 基础
    {
      title: t('views.settings.upgrade.basics'),
      data: [
        [
          tooltipFc(
            t('views.settings.upgrade.multi_platform_sync'),
            '',
            t('views.settings.upgrade.multi_platform_sync_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.full_text_search'),
            '',
            t('views.settings.upgrade.full_text_search_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(t('common.page_activity'), '', t('views.settings.upgrade.page_activity_desc')),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(t('common.page_history'), '', t('views.settings.upgrade.page_history_desc')),
          '',
          t('common.days_count', { count: 30 }),
          t('common.days_count', { count: 180 }),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.reference_suggestions'),
            '',
            t('views.settings.upgrade.reference_suggestions_desc')
          ),
          '',
          selected,
          selected,
        ],
        [tooltipFc(t('views.settings.upgrade.batch_file_download')), '', selected, selected],
      ],
    },
    // 分享与协作
    {
      title: t('views.settings.upgrade.share_and_collaboration'),
      data: [
        [
          tooltipFc(t('common.const.share_link'), '', t('views.settings.upgrade.share_link_desc')),
          isBuildIn() ? selected : '',
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.share_link_password'),
            '',
            t('views.settings.upgrade.share_link_password_desc')
          ),
          isBuildIn() ? selected : '',
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.custom_domain'),
            '',
            t('views.settings.upgrade.custom_domain_desc')
          ),
          isBuildIn() ? selected : '',
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.share_subscription'),
            '',
            t('views.settings.upgrade.share_subscription_desc')
          ),
          isBuildIn() ? selected : '',
          selected,
          selected,
        ],
        [tooltipFc(t('views.settings.upgrade.remove_share_ad')), '', selected, selected],
        [
          tooltipFc(
            t('views.settings.upgrade.single_page_external_collab'),
            '',
            t('views.settings.upgrade.single_page_external_collab_desc')
          ),
          '5',
          '100',
          '250',
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.public_template'),
            '',
            t('views.settings.upgrade.public_template_desc')
          ),
          '5',
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
        [tooltipFc(t('common.paid_subscribe')), '', selected, selected],
        [
          tooltipFc(
            t('common.public_homepage'),
            '',
            t('views.settings.upgrade.custom_domain_desc')
          ),
          '',
          selected,
          selected,
        ],
        [t('views.settings.upgrade.team_collaboration'), selected, selected, selected],
        [t('common.team_space'), selected, selected, selected],
      ],
    },
    // 安全与权限管理
    {
      title: t('views.settings.upgrade.security_and_permissions'),
      data: [
        [t('views.settings.upgrade.page_permissions'), selected, selected, selected],
        [t('views.settings.upgrade.space_member_management'), selected, selected, selected],
        [t('views.settings.upgrade.space_member_groups'), '', selected, selected],
        [
          <div className="whitespace-normal">
            {t('views.settings.upgrade.space_security_settings')}
          </div>,
          '',
          '',
          selected,
        ],
      ],
    },
    // 更多功能
    {
      title: t('views.settings.upgrade.more_features'),
      data: [
        [
          tooltipFc(
            t('views.settings.upgrade.collection_all_properties'),
            `${CURRENT_DOMAIN}/share/6a17bdee-5155-4569-9715-5ad894b0f4dd`,
            t('views.settings.upgrade.collection_all_properties_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.collection_all_views'),
            `${CURRENT_DOMAIN}/share/6a17bdee-5155-4569-9715-5ad894b0f4dd`,
            t('views.settings.upgrade.collection_all_views_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.collection_attachment'),
            '',
            t('views.settings.upgrade.collection_attachment_desc')
          ),
          '2MB',
          '5GB',
          '5GB',
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.file_folder_management'),
            `${CURRENT_DOMAIN}/share/fa4ab5ea-075f-4ccc-8e70-a112186310f3`,
            t('views.settings.upgrade.file_folder_management_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.reference_and_backlinks'),
            `${CURRENT_DOMAIN}/share/ef1a1829-baf5-4d92-bb9f-55a16e07219f`,
            t('views.settings.upgrade.reference_and_backlinks_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.file_preview'),
            `${CURRENT_DOMAIN}/share/771003b8-40d9-4f59-8d3a-99a690338fbe`,
            t('views.settings.upgrade.file_preview_desc')
          ),
          selected,
          selected,
          selected,
        ],
        // [tooltipFc('文件编辑（Office）'), '', selected, selected, selected],
        [tooltipFc(t('views.settings.upgrade.wechat_clip')), selected, selected, selected],
        [
          tooltipFc(
            t('views.settings.upgrade.all_templates'),
            `${CURRENT_DOMAIN}/template`,
            t('views.settings.upgrade.all_templates_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [t('views.settings.upgrade.dark_mode'), selected, selected, selected],
        [tooltipFc(t('views.settings.upgrade.font_page_layout')), '', selected, selected],
        [
          t('common.copy_to_space'),
          t('views.settings.upgrade.times_per_month', { count: 3 }),
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
      ],
    },
  ];

  const binPriceData = [
    // 用量
    {
      title: t('views.settings.upgrade.usage'),
      data: [
        [
          tooltipFc(t('views.settings.upgrade.page_block_count')),
          t('views.settings.upgrade.page_block_buildin_limit'),
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.space_members'),
            '',
            t('views.settings.upgrade.space_members_desc')
          ),
          t('common.unlimited_text'),
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.external_collaborators'),
            '',
            t('views.settings.upgrade.external_collaborators_desc')
          ),
          '5',
          '100',
          '1000',
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.space_storage_capacity'),
            '',
            t('views.settings.upgrade.space_storage_capacity_desc')
          ),
          t('views.settings.upgrade.capacity_2gb'),
          t('views.settings.upgrade.limit_1tb'),
          t('views.settings.upgrade.limit_1tb'),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.single_file_upload'),
            '',
            t('views.settings.upgrade.single_file_upload_desc')
          ),
          t('views.settings.upgrade.limit_5mb'),
          t('views.settings.upgrade.limit_5gb'),
          t('views.settings.upgrade.limit_5gb'),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.mindmap_node_limit'),
            '',
            t('views.settings.upgrade.mindmap_node_limit_desc')
          ),
          '300',
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
        [t('views.settings.upgrade.single_block_char_limit'), '100000', '100000', '100000'],
      ],
    },
    // 基础
    {
      title: t('views.settings.upgrade.basics'),
      data: [
        [
          tooltipFc(
            t('views.settings.upgrade.multi_platform_sync_short'),
            '',
            t('views.settings.upgrade.multi_platform_sync_desc_short')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.full_text_search_short'),
            '',
            t('views.settings.upgrade.full_text_search_desc_short')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('common.page_activity'),
            '',
            t('views.settings.upgrade.page_activity_desc_short')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('common.page_history'),
            '',
            t('views.settings.upgrade.page_history_desc_short')
          ),
          '',
          t('common.days_count', { count: 30 }),
          t('common.days_count', { count: 180 }),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.reference_suggestions_short'),
            '',
            t('views.settings.upgrade.reference_suggestions_desc_short')
          ),
          '',
          selected,
          selected,
        ],
        [tooltipFc(t('views.settings.upgrade.batch_file_download')), '', selected, selected],
      ],
    },
    // 分享与协作
    {
      title: t('views.settings.upgrade.share_and_collaboration'),
      data: [
        [
          tooltipFc(t('common.const.share_link'), '', t('views.settings.upgrade.share_link_desc')),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.share_link_password'),
            '',
            t('views.settings.upgrade.share_link_password_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.custom_domain'),
            '',
            t('views.settings.upgrade.custom_domain_buildin_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [t('views.settings.upgrade.remove_share_ad'), '', selected, selected],
        [
          tooltipFc(
            t('views.settings.upgrade.public_template'),
            '',
            t('views.settings.upgrade.public_template_desc_short')
          ),
          '5',
          t('common.unlimited_text'),
          t('common.unlimited_text'),
        ],
        [tooltipFc(t('common.paid_subscribe')), '', selected, selected],
        [t('common.public_homepage'), '', selected, selected],
        [t('views.settings.upgrade.team_collaboration'), selected, selected, selected],
        [t('common.team_space'), selected, selected, selected],
      ],
    },
    // 安全与权限管理
    {
      title: t('views.settings.upgrade.security_and_permissions'),
      data: [
        [t('views.settings.upgrade.page_permissions'), selected, selected, selected],
        [t('views.settings.upgrade.space_member_management'), selected, selected, selected],
        [t('views.settings.upgrade.space_member_groups'), '', selected, selected],
      ],
    },
    // 更多功能
    {
      title: t('views.settings.upgrade.more_features'),
      data: [
        [
          tooltipFc(
            t('views.settings.upgrade.collection_all_properties'),
            '',
            t('views.settings.upgrade.collection_all_properties_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.collection_all_views'),
            '',
            t('views.settings.upgrade.collection_all_views_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.collection_attachment_upload'),
            '',
            t('views.settings.upgrade.collection_attachment_upload_desc')
          ),
          t('views.settings.upgrade.limit_5mb'),
          t('views.settings.upgrade.limit_5gb'),
          t('views.settings.upgrade.limit_5gb'),
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.file_folder_management'),
            '',
            t('views.settings.upgrade.file_folder_management_desc_short')
          ),
          selected,
          selected,
          selected,
        ],
        [
          tooltipFc(
            t('views.settings.upgrade.file_preview_office'),
            ``,
            t('views.settings.upgrade.file_preview_desc')
          ),
          selected,
          selected,
          selected,
        ],
        [t('views.settings.upgrade.dark_mode'), selected, selected, selected],
        [t('views.settings.upgrade.font_page_layout'), '', selected, selected],
      ],
    },
  ];

  if (__BUILD_IN__) {
    return binPriceData;
  }

  return fuPriceData;
};

const useAiPlanPrice = () => {
  const { t } = useAppTranslation();

  const tooltipFc = (str: string, link?: string, popup?: string) => (
    <Tooltip popup={popup} className={cx(link && 'cursor-pointer border-b border-grey6')}>
      <a href={link ? link : undefined} target="_blank" rel="noreferrer">
        {str}
      </a>
    </Tooltip>
  );

  return [
    {
      title: t('views.settings.upgrade.usage'),
      data: [
        [
          tooltipFc(t('views.settings.upgrade.ai_requests')),
          t('views.settings.upgrade.per_month_count', { count: 10 }),
          t('views.settings.upgrade.per_month_count', { count: 3000 }),
        ],
        // [
        //   tooltipFc('阅读 PDF'),
        //   '每月 2 文件\n每文件 200 页以内',
        //   '每天 50 文件\n每文件 2000 页以内',
        // ],
      ],
    },
  ];
};

interface PriceTableProps {
  type: 'spacePlan' | 'ai';
}
export const PriceTable: FC<PriceTableProps> = (props) => {
  const { type = 'spacePlan' } = props;
  const spacePlanPrice = useSpacePlanPrice();
  const aiPlanPrice = useAiPlanPrice();

  const priceData = useMemo(() => {
    if (type === 'spacePlan') return spacePlanPrice;
    if (type === 'ai') return aiPlanPrice;
    return spacePlanPrice;
  }, [aiPlanPrice, spacePlanPrice, type]);

  return (
    <>
      {/* 价格表 */}
      {priceData.map((planData, dataIndex) => {
        return (
          <Fragment key={dataIndex}>
            <div className="border-grey5 border-b h-9 px-2 flex items-center text-t2-medium text-grey3">
              {planData.title}
            </div>
            {planData.data.map((item, index) => (
              <div
                key={`${dataIndex}-${index}`}
                className={cx(
                  'text-t2 border-grey5 border-b grid',
                  dataIndex === priceData.length - 1 &&
                    index === planData.data.length - 1 &&
                    'border-none'
                )}
                style={{
                  gridTemplateColumns: `repeat(${item.length}, minmax(0, 1fr))`,
                }}
              >
                {item.map((value, valueIndex) => (
                  <span
                    key={valueIndex}
                    className={cx(
                      'flex items-center px-2 flex-shrink-0 py-2 min-h-[36px] border-grey5 border-l whitespace-pre-line',
                      valueIndex === 0 && 'text-grey3 border-l-0',
                      valueIndex % 2 !== 0 && 'bg-black_003'
                    )}
                  >
                    {value}
                  </span>
                ))}
              </div>
            ))}
          </Fragment>
        );
      })}
    </>
  );
};
